// SignedUpload — pushes the selected file directly to GCS (or to the dev
// local-storage controller in dev) using the signed PUT URL the LiveView
// minted server-side via `Seiva.Storage.generate_upload_signed_url/2`.
//
// Phoenix is bypassed for the bytes — the only client→server hop is the
// initial "give me a URL" event + the final "I'm done" event. Lets the
// Workflow Test Mode and `data_transform` node accept multi-GB CSV/Parquet
// without hitting Plug.Parsers' 1 GB multipart cap.
//
// Server contract:
//   pushEventTo(this.el, "request_signed_upload",
//     { field_name, filename, content_type, size_bytes })
//     → LV replies via push_event "signed_upload_ready"
//       { field_name, file_id, signed_put_url, content_type }
//   PUT signed_put_url with body=file, Content-Type=content_type
//   pushEventTo(this.el, "signed_upload_completed",
//     { field_name, file_id, filename, size_bytes })  — or "_failed"
//
// DOM:
//   <div phx-hook="SignedUpload" id="signed-upload-<field>"
//        data-field-name="..." data-max-bytes="...">
//     <input type="file" data-role="picker" />
//     <progress data-role="progress" value="0" max="100"></progress>
//     <span data-role="status"></span>
//   </div>

export default {
  mounted() {
    this.fieldName = this.el.dataset.fieldName;
    this.maxBytes = parseInt(this.el.dataset.maxBytes || "10000000000", 10); // 10 GB

    this.picker = this.el.querySelector('[data-role="picker"]');
    this.progress = this.el.querySelector('[data-role="progress"]');
    this.status = this.el.querySelector('[data-role="status"]');

    if (!this.picker) {
      console.warn("[SignedUpload] missing <input data-role='picker'>");
      return;
    }

    this.onPick = this.onPick.bind(this);
    this.onSignedUploadReady = this.onSignedUploadReady.bind(this);
    this.picker.addEventListener("change", this.onPick);

    this.handleEvent("signed_upload_ready", this.onSignedUploadReady);
  },

  destroyed() {
    if (this.picker) this.picker.removeEventListener("change", this.onPick);
  },

  onPick(ev) {
    const file = ev.target.files && ev.target.files[0];
    if (!file) return;

    if (file.size > this.maxBytes) {
      this.setStatus(`File too large (${humanBytes(file.size)} > ${humanBytes(this.maxBytes)})`);
      return;
    }

    this.pendingFile = file;
    this.setStatus("Preparing upload…");
    this.setProgress(0);

    this.pushEventTo(this.el, "request_signed_upload", {
      field_name: this.fieldName,
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      size_bytes: file.size,
    });
  },

  onSignedUploadReady(payload) {
    if (!this.pendingFile || payload.field_name !== this.fieldName) return;
    if (!payload.signed_put_url || !payload.file_id) {
      this.setStatus("Server didn't return a signed URL");
      this.pendingFile = null;
      return;
    }

    const file = this.pendingFile;
    const contentType = payload.content_type || file.type || "application/octet-stream";

    const xhr = new XMLHttpRequest();
    xhr.open("PUT", payload.signed_put_url, true);
    xhr.setRequestHeader("Content-Type", contentType);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) this.setProgress((e.loaded / e.total) * 100);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        this.setStatus(`Uploaded ${humanBytes(file.size)}`);
        this.setProgress(100);
        this.pushEventTo(this.el, "signed_upload_completed", {
          field_name: this.fieldName,
          file_id: payload.file_id,
          filename: file.name,
          size_bytes: file.size,
        });
      } else {
        this.onFailed(`Upload failed (HTTP ${xhr.status})`);
      }
      this.pendingFile = null;
    };

    xhr.onerror = () => {
      this.onFailed("Network error during upload");
      this.pendingFile = null;
    };

    xhr.send(file);
    this.setStatus(`Uploading ${humanBytes(file.size)}…`);
  },

  onFailed(message) {
    this.setStatus(message);
    this.setProgress(0);
    this.pushEventTo(this.el, "signed_upload_failed", {
      field_name: this.fieldName,
      reason: message,
    });
  },

  setStatus(msg) {
    if (this.status) this.status.textContent = msg;
  },

  setProgress(pct) {
    if (this.progress) this.progress.value = pct;
  },
};

function humanBytes(n) {
  if (!Number.isFinite(n)) return "?";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}
